// Smelting resolution + fuel table (M6.2). PURE (ENG-1).
//
// A thin registry-facing layer the furnace block entity ticks against: what a
// stack smelts into (and how long / how much XP), and how many game ticks a fuel
// item burns. Recipe data lives in data/recipes/*smelting* (§3.3); fuel burn time
// is the item's `fuelTicks` field (BLK-6). Both are looked up here so the furnace
// logic (containers/furnace.ts) stays a pure state machine over injected numbers.
import type { Registries } from '../registry/load';
import type { RecipeDef } from '../registry/schemas';
import { type ItemStack, isEmpty } from './inventory';
import { ingredientMatches } from './crafting';

/** Default furnace cook time (game ticks) when a recipe omits `time`. */
export const FURNACE_COOK_TICKS = 200;

export interface SmeltResult {
  /** Result item id. */
  item: number;
  /** Cook time in game ticks (recipe `time`, default 200). */
  time: number;
  /** XP granted per item smelted (accrues in the furnace, released on collect). */
  xp: number;
}

/** The kinds a given furnace family accepts (smelting = the classic furnace). */
export type CookKind = 'smelting' | 'blasting' | 'smoking' | 'campfire';

/** Resolve the smelt of one input stack, or null if it can't be smelted. */
export function smeltResult(
  reg: Registries,
  input: ItemStack,
  kinds: readonly CookKind[] = ['smelting'],
): SmeltResult | null {
  if (isEmpty(input)) return null;
  for (const recipe of reg.recipes.list) {
    if (!isCookRecipe(recipe) || !kinds.includes(recipe.type as CookKind)) continue;
    if (!ingredientMatches(reg, recipe.ingredient, input)) continue;
    const item = reg.items.idOf.get(recipe.result.item);
    if (item === undefined) continue;
    return { item, time: recipe.time ?? FURNACE_COOK_TICKS, xp: recipe.xp ?? 0 };
  }
  return null;
}

function isCookRecipe(r: RecipeDef): r is Extract<RecipeDef, { type: 'smelting' | 'blasting' | 'smoking' | 'campfire' }> {
  return r.type === 'smelting' || r.type === 'blasting' || r.type === 'smoking' || r.type === 'campfire';
}

/** Burn time (game ticks) of a fuel item, or 0 if it isn't a fuel. */
export function fuelTicks(reg: Registries, itemId: number): number {
  const name = reg.items.nameOf[itemId];
  if (name === undefined) return 0;
  return reg.items.byName.get(name)?.fuelTicks ?? 0;
}

/** Is `stack` a usable fuel (for the furnace fuel-slot filter)? */
export function isFuel(reg: Registries, stack: ItemStack): boolean {
  return !isEmpty(stack) && fuelTicks(reg, stack.item) > 0;
}
